import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, Paper, Tabs, Tab, Menu, MenuItem } from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import '../common/commonStyles.css';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../Config';
import { toast } from 'sonner';
import moment from 'moment';
import UpcomingNotifications from './UpcomingNotifications';
import PastNotifications from './PastNotifications';

const extractNotificationsArray = (json) => {
    if (!json || typeof json !== 'object') return [];
    if (Array.isArray(json)) return json;
    if (Array.isArray(json.data)) return json.data;
    if (Array.isArray(json.data?.notifications)) return json.data.notifications;
    if (Array.isArray(json.data?.rows)) return json.data.rows;
    if (Array.isArray(json.data?.items)) return json.data.items;
    if (Array.isArray(json.notifications)) return json.notifications;
    return [];
};

const normalizeRow = (raw) => {
    const id = raw.id ?? raw.notification_id ?? raw.uid;
    const title = String(raw.title ?? raw.notification_title ?? '');
    const message = String(raw.message ?? raw.notification_message ?? '');
    const scheduleDateRaw =
        raw.scheduled_date ??
        raw.schedule_date ??
        raw.scheduled_at ??
        raw.schedule_at ??
        raw.scheduled_datetime ??
        raw.scheduledDate ??
        raw.scheduleDate ??
        raw.notification_scheduled_date ??
        raw.notification_schedule_date;
    const scheduleTimeRaw =
        raw.scheduled_time ??
        raw.schedule_time ??
        raw.scheduledTime ??
        raw.scheduleTime;
    const parsedTime = scheduleTimeRaw
        ? moment(String(scheduleTimeRaw), ['HH:mm', 'HH:mm:ss', moment.ISO_8601], true)
        : null;
    let scheduledMoment = moment.invalid();
    if (scheduleDateRaw) {
        const d = String(scheduleDateRaw).split('T')[0];
        if (parsedTime?.isValid()) {
            scheduledMoment = moment(
                `${d} ${parsedTime.format('HH:mm:ss')}`,
                'YYYY-MM-DD HH:mm:ss',
                true
            );
        }
        if (!scheduledMoment.isValid()) {
            scheduledMoment = moment(scheduleDateRaw);
        }
        if (!scheduledMoment.isValid()) {
            scheduledMoment = moment(
                String(scheduleDateRaw),
                ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD-MM-YYYY'],
                true
            );
        }
    } else if (parsedTime?.isValid()) {
        scheduledMoment = parsedTime.clone();
    }
    const sendLower = String(
        raw.send ?? raw.schedule_type ?? raw.send_mode ?? ''
    ).toLowerCase();
    const isScheduledOn =
        sendLower === 'later' ||
        sendLower === 'scheduled' ||
        (sendLower !== 'now' && sendLower !== 'immediate' && sendLower === '' && scheduledMoment.isValid());
    const statusRaw = String(raw.status ?? raw.notification_status ?? raw.state ?? '').toLowerCase();
    const explicitPast =
        statusRaw === 'past' ||
        statusRaw === 'completed' ||
        statusRaw === 'inactive' ||
        statusRaw === 'expired' ||
        statusRaw === 'sent' ||
        statusRaw === 'delivered' ||
        statusRaw === 'failed' ||
        raw.is_past === true ||
        raw.is_past === 1 ||
        raw.is_past === '1';
    const explicitUpcoming =
        statusRaw === 'upcoming' ||
        statusRaw === 'scheduled' ||
        statusRaw === 'pending' ||
        raw.is_upcoming === true ||
        raw.is_upcoming === 1;
    let isPast = false;
    if (explicitPast) isPast = true;
    else if (explicitUpcoming) isPast = false;
    else if (isScheduledOn && scheduledMoment.isValid()) isPast = scheduledMoment.isBefore(moment());
    else isPast = false;
    const hasExplicitTimeField = Boolean(scheduleTimeRaw);
    const hasTimeInDateString =
        scheduleDateRaw && /T\d{1,2}:\d{2}/.test(String(scheduleDateRaw));
    const scheduledDate = scheduledMoment.isValid()
        ? hasExplicitTimeField || hasTimeInDateString
            ? scheduledMoment.format('DD-MMM-YYYY HH:mm')
            : scheduledMoment.format('DD-MMM-YYYY')
        : '';
    const statusSource = String(
        raw.status ?? raw.notification_status ?? raw.state ?? ''
    ).trim();
    const statusLabel = statusSource
        ? statusSource.charAt(0).toUpperCase() +
          statusSource.slice(1).toLowerCase().replace(/_/g, ' ')
        : '—';
    return {
        id,
        title,
        message,
        status: statusLabel,
        scheduledDate,
        isPast,
    };
};

export default function Notification() {
    const [tabValue, setTabValue] = useState(0);
    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const [selectedNotificationId, setSelectedNotificationId] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setListLoading(false);
                return;
            }
            setListLoading(true);
            try {
                const response = await fetch(`${API_URL.replace(/\/+$/, '')}/notifications`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const result = await response.json();
                if (cancelled) return;
                if (!response.ok) {
                    toast.error(result?.message || 'Failed to load notifications');
                    setNotifications([]);
                    return;
                }
                const list = extractNotificationsArray(result);
                const normalized = list.map(normalizeRow).filter((r) => r.id != null && r.id !== '');
                if (result?.success === false && normalized.length === 0) {
                    toast.error(result?.message || 'Failed to load notifications');
                }
                setNotifications(normalized);

            } catch {
                if (!cancelled) {
                    toast.error('Failed to load notifications');
                    setNotifications([]);
                }
            } finally {
                if (!cancelled) setListLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const rows = useMemo(() => {
        if (tabValue === 0) {
            return notifications.filter((item) => !item.isPast);
        }
        return notifications.filter((item) => item.isPast);
    }, [tabValue, notifications]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        handleCloseActionMenu();
    };

    const handleOpenActionMenu = (event, id) => {
        setActionAnchorEl(event.currentTarget);
        setSelectedNotificationId(id);
    };

    const handleCloseActionMenu = () => {
        setActionAnchorEl(null);
        setSelectedNotificationId(null);
    };

    const handleEdit = () => {
        if (!selectedNotificationId) {
            handleCloseActionMenu();
            return;
        }
        handleCloseActionMenu();
        const idNum = Number(selectedNotificationId);
        if (!Number.isFinite(idNum)) {
            toast.error('Invalid notification id');
            return;
        }
        navigate(`/Notifications/EditNotification?id=${idNum}`);
    };

    const handleDelete = async () => {
        if (!selectedNotificationId) {
            handleCloseActionMenu();
            return;
        }
        const idToDelete = String(selectedNotificationId);
        const idParam = encodeURIComponent(idToDelete);
        handleCloseActionMenu();
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error('Not authenticated');
            return;
        }
        try {
            const response = await fetch(
                `${API_URL.replace(/\/+$/, '')}/notifications/delete/${idParam}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                toast.error(result?.message || 'Failed to delete notification');
                return;
            }
            setNotifications((prev) => prev.filter((n) => String(n.id) !== idToDelete));
            toast.success(result?.message || 'Notification deleted');
        } catch {
            toast.error('Failed to delete notification');
        }
    };

    return (
        <Box className="page-container">
            <Paper className="paper-style">
                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.2 } }}>
                    <Box
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        gap={{ xs: 1.5, sm: 2 }}
                    >
                        <Typography className="page-title" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>
                            Notifications List
                        </Typography>
                        <Button
                            variant="contained"
                            className="button-variant-contained"
                            startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                            onClick={() => navigate('/Notifications/CreateNotification')}
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                alignSelf: { xs: 'stretch', sm: 'auto' },
                                py: { xs: 1.25, sm: 1 },
                                flexShrink: 0,
                            }}
                        >
                            Create New
                        </Button>
                    </Box>
                    <Box sx={{ mt: { xs: 1, sm: 0.5 }, mx: { xs: -0.5, sm: 0 } }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                minHeight: 36,
                                '& .MuiTabs-scrollButtons.Mui-disabled': {
                                    opacity: 0.35,
                                },
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontWeight: 500,
                                    minHeight: 36,
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    px: { xs: 1, sm: 2 },
                                },
                                "& .MuiTabs-indicator": {
                                    backgroundColor: "#F8991D",
                                },
                                "& .MuiTab-root.Mui-selected": {
                                    color: "#F8991D",
                                },
                            }}
                        >
                            <Tab label="Upcoming Notifications" />
                            <Tab label="Past Notifications" />
                        </Tabs>
                    </Box>
                </Box>

                {tabValue === 0 ? (
                    <UpcomingNotifications
                        rows={rows}
                        listLoading={listLoading}
                        onOpenActionMenu={handleOpenActionMenu}
                    />
                ) : (
                    <PastNotifications
                        rows={rows}
                        listLoading={listLoading}
                    />
                )}
                <Menu
                    anchorEl={actionAnchorEl}
                    open={Boolean(actionAnchorEl)}
                    onClose={handleCloseActionMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: { minWidth: 160, maxWidth: 'calc(100vw - 16px)' },
                        },
                    }}
                >
                    {tabValue === 0 && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
                    {tabValue === 0 && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
                </Menu>
            </Paper>
        </Box>
    );
}